import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    heading = "heading",
    subHeading = "subHeading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.heading]: string;
        [fields.subHeading]: string;
    };
}

export interface CENSectionStatisticsStyle {
    container?: InjectableCss;
    subContainer: InjectableCss;
    heading?: InjectableCss;
    subHeading?: InjectableCss;
    img: InjectableCss;
}

export const CENsectionStatisticsStyles: CENSectionStatisticsStyle = {
    container: {
        css: css`
            width: 100%;
            position: relative;
            margin-bottom: 104px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 76px;
                    `,
                ]);
            }}
        `,
    },
    subContainer: {
        css: css`
            position: absolute;
            top: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            top: 20px;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    heading: {
        css: css`
            p {
                width: 100%;
                margin-bottom: 16px !important;
                font-size: 56px;
                font-weight: ${TYPOGRAPHY.weight.black};
                line-height: 64px;
                color: ${COLORS.primary.base};
                text-align: center;
            }
        `,
    },
    subHeading: {
        css: css`
            p {
                width: 100%;
                text-align: center;
                font-size: ${TYPOGRAPHY.size.largest};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 32px;
                color: ${COLORS.black};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${TYPOGRAPHY.size.large};
                                line-height: 28px;
                                padding: 0px 8px;
                            `,
                            css`
                                padding: 0px 20px;
                            `,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    img: {
        css: css`
            width: 100%;
            display: flex;
            height: 224px;
            align-items: stretch;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 200px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
};

const styles = CENsectionStatisticsStyles;

export const SectionStaticWidget: FC<OwnProps> = ({ fields }) => {
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.subContainer}>
                <Typography {...styles.heading}> {parse(transformContentHtml(fields.heading), parserOptions)}</Typography>
                <Typography {...styles.subHeading}>{parse(transformContentHtml(fields.subHeading), parserOptions)}</Typography>
            </StyledWrapper>
            <Img src={fields.image} loading="lazy" altText={fields.heading} {...styles.img} />
        </StyledWrapper>
    );
};

const sectionStatistics: WidgetModule = {
    component: SectionStaticWidget,
    definition: {
        group: "Basic",
        displayName: "Section Statistics",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.subHeading,
                editorTemplate: "RichTextField",
                displayName: "Sub Heading",
                defaultValue: "",
            },
        ],
    },
};

export default sectionStatistics;
